import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client';
import { Alert, Button, Chip, Table } from '../../ui';
import { buildSave, draftOf, isDirty, problemWith, type Draft } from './production-model';

export interface ProductionRow {
  id: string; lineNumber: number; variantCode: string; description: string | null; size: string | null;
  ordered: number; made: number; outstanding: number; shortfall: number | null;
}

/**
 * A-26 — one order's production lines, with Made editable in place and one Save for the order.
 *
 * Made can only go up; the Job Numbers of the pieces added are generated by the server. Rows with unsaved changes are marked, Save is refused while any row is invalid,
 * and a failed save keeps everything typed. Leaving the page (or following a link) with unsaved changes asks first.
 */
export function ProductionTable(
  { orderId, reference, state, lines, mayEdit, onSaved }:
  { orderId: string; reference: string; state: string; lines: ProductionRow[]; mayEdit: boolean; onSaved: () => Promise<unknown> | void },
) {
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [saving, setSaving] = useState(false);
  // After the save has gone through, while the board is re-read: the typed values stay on screen until it lands.
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const draftFor = (l: ProductionRow): Draft => drafts[l.id] ?? draftOf(l);
  const set = (l: ProductionRow, patch: Partial<Draft>) => {
    setDone(null);
    setDrafts((prev) => ({ ...prev, [l.id]: { ...(prev[l.id] ?? draftOf(l)), ...patch } }));
  };

  const dirtyLines = lines.filter((l) => isDirty(l, draftFor(l)));
  const problems = new Map(dirtyLines.map((l) => [l.id, problemWith(l, draftFor(l))] as const));
  const invalid = [...problems.values()].some((p) => p !== null);
  const dirty = dirtyLines.length > 0;
  const madeEditable = mayEdit && state === 'IN_MANUFACTURING';

  // Unsaved work is not lost to a refresh, a closed tab or a click on an in-app link without a word.
  useEffect(() => {
    if (!dirty) return undefined;
    const beforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    const onClick = (e: MouseEvent) => {
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || a.target === '_blank' || a.origin !== window.location.origin) return;
      if (!window.confirm('You have unsaved Made changes. Leave without saving?')) { e.preventDefault(); e.stopPropagation(); }
    };
    window.addEventListener('beforeunload', beforeUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty]);

  async function save() {
    const rows = buildSave(lines, drafts);
    if (!rows || invalid || saving || refreshing) return;
    setSaving(true); setError(null); setDone(null);
    try {
      const r = await api.post<{ created: number }>(
        `/manufacturing/orders/${orderId}/production`, { lines: rows },
      );
      // Say so as soon as the server has it; the board re-read that follows takes a moment more.
      setDone(`Saved for ${reference}: ${r.created} piece${r.created === 1 ? '' : 's'} recorded as made.`);
    } catch (e) {
      // Nothing typed is discarded: the drafts stay exactly as they were.
      setError(e instanceof ApiError ? e.message : 'The changes could not be saved. Check your connection and try again.');
      return;
    } finally {
      setSaving(false);
    }
    /*
     * The save is done and has said so. The board re-read is a SEPARATE step, outside that
     * try: a re-read that fails is not a save that failed, and reporting it as one left the
     * rows marked "Unsaved" over values the server already had — the operator's only reading
     * of "did my work land?". The typed values are cleared either way; the board catches up on
     * its next read.
     */
    setRefreshing(true);
    try { await onSaved(); } finally { setDrafts({}); setRefreshing(false); }
  }

  return (
    <>
      <Table<ProductionRow>
        rows={lines}
        rowKey={(l) => l.id}
        rowClassName={(l) => (isDirty(l, draftFor(l)) ? 'on' : undefined)}
        columns={[
          {
            header: '#', numeric: true,
            render: (l) => (
              <>
                {l.lineNumber}
                {isDirty(l, draftFor(l)) ? <div><Chip tone="us">Unsaved</Chip></div> : null}
              </>
            ),
          },
          { header: 'Piece', render: (l) => <>{l.description ?? l.variantCode} <span className="mk-dim" style={{ overflowWrap: 'anywhere' }}>{l.variantCode}</span></> },
          { header: 'Size', render: (l) => l.size ?? <span className="mk-dim">—</span> },
          { header: 'Ordered', numeric: true, render: (l) => l.ordered },
          {
            header: 'Made', numeric: true,
            render: (l) => {
              if (!madeEditable || l.ordered === l.made) return l.made;
              const d = draftFor(l);
              const p = problems.get(l.id);
              return (
                <div style={{ display: 'grid', gap: 4, justifyItems: 'end' }}>
                  <input
                    className="mk-fld" type="number" inputMode="numeric" min={l.made} max={l.ordered} step={1} style={{ width: 80 }}
                    aria-label={`Made, line ${l.lineNumber}`} value={d.made}
                    onChange={(e) => set(l, { made: e.target.value })}
                  />
                  {p ? <span role="alert" className="mk-dim" style={{ maxWidth: 220, textAlign: 'right', color: 'var(--mk-st-stop-ink, inherit)' }}>{p}</span> : null}
                </div>
              );
            },
          },
          {
            header: 'Still to make / short', numeric: true,
            render: (l) => (l.shortfall === null ? Math.max(l.ordered - (Number(draftFor(l).made) || l.made), 0)
              : l.shortfall > 0 ? <Chip tone="stop">{l.shortfall} short</Chip> : <span className="mk-dim">complete</span>),
          },
        ]}
      />
      {error ? <Alert tone="stop">{error}</Alert> : null}
      {done ? <Alert tone="done">{done}</Alert> : null}
      {mayEdit ? (
        <div style={{ display: 'flex', gap: 'var(--mk-space-3)', alignItems: 'center', marginTop: 'var(--mk-space-2)' }}>
          <Button size="sm" disabled={!dirty || invalid || saving || refreshing} onClick={() => void save()}>
            {saving ? 'Saving…' : refreshing ? 'Updating…' : 'Save'}
          </Button>
          <Button size="sm" tone="ghost" disabled={!dirty || saving || refreshing} onClick={() => { setDrafts({}); setError(null); }}>Discard changes</Button>
          {dirty ? <span className="mk-dim">{dirtyLines.length} row{dirtyLines.length === 1 ? '' : 's'} with unsaved changes</span> : null}
        </div>
      ) : null}
    </>
  );
}
